/**
 * Entity-editor registry — the React equivalent of the prototype's
 * `window.__entityEditors` / `registerEntityEditor` / `confirmDiscardEntityIfDirty`
 * pattern (see `<main>/Prototyping/Agent Studio/agent-studio.html`).
 *
 * Every Skill, Connection, and Knowledge Base source editor (Agent scope and
 * Global scope — six editor "kinds" in total) registers an `EntityEditor` here
 * while it is mounted. Call sites that live outside the editor's own component
 * subtree — TopBar (brand click, Sign Out), left-nav, and breadcrumb links —
 * consult this registry via `hasDirtyOpenEntityEditor()` before allowing
 * navigation to proceed. Refresh/tab-close is guarded separately by
 * `useBeforeUnloadGuard`, which consults this same registry.
 *
 * Implemented as a module-level singleton (not a hook/Context instance) so it
 * is always available regardless of React tree position or mount order — the
 * same "must be available before consumers run" concern the prototype's
 * static-load-order comment calls out, solved here by not depending on React
 * lifecycle at all for the registry itself.
 */

export interface EntityEditor {
  /** Whether the editor is currently open/visible (a closed editor never blocks navigation). */
  isOpen: () => boolean
  /** Whether the open editor has unsaved changes relative to its last snapshot. */
  isDirty: () => boolean
  /** Reset the editor's dirty snapshot (and/or close it) after a confirmed discard. */
  markClean: () => void
}

const editors = new Set<EntityEditor>()

/** Registers an editor for as long as it stays mounted/open. Returns an unregister function. */
export function registerEntityEditor(editor: EntityEditor): () => void {
  editors.add(editor)
  return () => {
    editors.delete(editor)
  }
}

/** True if any registered editor is both open and dirty. A misbehaving editor never crashes the check. */
export function hasDirtyOpenEntityEditor(): boolean {
  for (const editor of editors) {
    try {
      if (editor.isOpen() && editor.isDirty()) return true
    } catch {
      // Defensive — one broken editor must never block navigation for the whole app.
    }
  }
  return false
}

/** Marks every currently dirty, open editor clean — used when the user confirms "discard". */
export function markAllEntityEditorsClean(): void {
  for (const editor of editors) {
    try {
      if (editor.isOpen() && editor.isDirty()) editor.markClean()
    } catch {
      // Best-effort — one editor's failure shouldn't stop the others from clearing.
    }
  }
}

/** Test-only escape hatch to reset global state between test cases. */
export function _resetEntityEditorRegistryForTests(): void {
  editors.clear()
}
